/**
 * ConfirmDialog — reusable confirmation modal based on Chakra UI AlertDialog.
 *
 * Replaces native `window.confirm()` with a styled, consistent modal
 * that matches the application design. Can be used anywhere a yes/no
 * confirmation is needed.
 */
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react"
import { useRef } from "react"

export type ConfirmDialogProps = {
  /** Whether the dialog is visible. */
  isOpen: boolean
  /** Called when the user dismisses or cancels the dialog. */
  onClose: () => void
  /** Called when the user confirms the action. */
  onConfirm: () => void
  /** Dialog header text (default: "Potwierdzenie"). */
  title?: string
  /** Dialog body / message. */
  message: string
  /** Label for the confirm button (default: "Potwierdź"). */
  confirmLabel?: string
  /** Label for the cancel button (default: "Anuluj"). */
  cancelLabel?: string
  /** Colour scheme for the confirm button (default: "red"). */
  confirmColorScheme?: string
  /** Whether the confirm action is in progress. */
  isLoading?: boolean
}

export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title = "Potwierdzenie",
  message,
  confirmLabel = "Potwierdź",
  cancelLabel = "Anuluj",
  confirmColorScheme = "red",
  isLoading = false,
}: ConfirmDialogProps) {
  const cancelRef = useRef<HTMLButtonElement | null>(null)

  return (
    <AlertDialog
      isOpen={isOpen}
      onClose={onClose}
      leastDestructiveRef={cancelRef}
      size={{ base: "sm", md: "md" }}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>{title}</AlertDialogHeader>

          <AlertDialogBody>{message}</AlertDialogBody>

          <AlertDialogFooter gap={3}>
            <Button ref={cancelRef} onClick={onClose} isDisabled={isLoading}>
              {cancelLabel}
            </Button>
            <Button
              colorScheme={confirmColorScheme}
              onClick={onConfirm}
              isLoading={isLoading}
            >
              {confirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
